import { useLocale } from '@aviv-unified-frontend/runtime/i18n';
import { CustomEventService, IEvents } from '@mfe-helpers/customEvent';
import submitFormattedForm from '@mfe-helpers/formHelpers';
import { goToLandingPage } from '@mfe-helpers/goToLandingPage';
import { composeInteractionPayload, composeMPUInteractionPayload } from '@mfe-helpers/tracking';
import { ResultStatus, ValidationMethods } from '@mfe-presentation/constants/userVerification';
import { useAppContext, useAppDispatch } from '@mfe-presentation/contexts';
import { initialState } from '@mfe-presentation/contexts/initialState';
import useStepUtils from '@mfe-presentation/hooks/useStepUtils';
import { EActionType, IUserVerification } from '@mfe-types/context';
import {
    EAvCategory,
    EInteractionEvents,
    EMonthlyPriceUpdateInteractionEvents,
} from '@mfe-types/tracking';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { extractPhoneNumber } from '@aviv-estimation-funnel/shells/src/app/helpers/extractPhoneNumber';

const useNavigation = () => {
    const {
        currentStep: { name, index, customSubmit },
        maxStepIndex,
        config,
        userVerification,
        estimations,
        stepNames,
        formValues,
        portal,
        timer,
        isFromUpdatePage,
    } = useAppContext();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { isTimerRunning, startTimer, stopTimer } = timer;
    const { locale } = useLocale();

    const { setStepAndNavigate } = useStepUtils();

    const goTo = (nextStepIndex: number) => {
        if (nextStepIndex < 0) {
            return;
        }

        setStepAndNavigate({ nextStepIndex });
    };

    const goNext = () => {
        const nextStepIndex = index + 1;
        if (nextStepIndex <= maxStepIndex) {
            setStepAndNavigate({ nextStepIndex });
        }
    };

    const backToUpdatePage = (callFromSiblingRoute: boolean) => {
        // As the update page sets its own step name, we can access it directly through the URL.
        const relativePrefix = callFromSiblingRoute ? '../' : '';
        navigate(`${relativePrefix}update?${searchParams}`);
    };

    const goBack = (callFromSiblingRoute: boolean) => {
        if (!formValues.estimationId && index === 0) {
            goToLandingPage();
            return;
        }
        // if we have an estimationId it means that we come from the updatePage
        if (
            formValues.estimationId &&
            (index === 0 || index === stepNames.indexOf('verification'))
        ) {
            backToUpdatePage(callFromSiblingRoute);
            return;
        }
        setStepAndNavigate({
            nextStepIndex: index - 1,
            hasRelativePath: callFromSiblingRoute,
        });
    };

    const goToFirstPage = () => {
        setStepAndNavigate({ nextStepIndex: 0 });
    };

    const handleBack = (callFromSiblingRoute = true) => {
        if (location.pathname.includes('/loading')) {
            return;
        }

        if (location.pathname.includes('/result')) {
            window.location.href = `${process.env.NX_PORTAL_URL}`;
            return;
        }

        if (userVerification.isFetching || estimations.isFetching) {
            return;
        }

        if (customSubmit) {
            switch (name) {
                case 'identity': {
                    // TODO check if we should keep this previous reset to use v1 mtan ?
                    // dispatch({
                    //     type: EActionType.SetUserVerification,
                    //     payload: initialState.userVerification,
                    // });
                    dispatch({
                        type: EActionType.SetEstimations,
                        payload: initialState.estimations,
                    });
                    break;
                }
                case 'verification': {
                    // Normally, this case doesn't exist. User can't go back from estimation page
                    dispatch({
                        type: EActionType.SetEstimations,
                        payload: initialState.estimations,
                    });
                    break;
                }
            }
        }
        // TODO: Investigate why clicking the back button doesn't close the toast message.
        // In the meantime, ensure that persistent toasts are closed when the user navigates back.
        dispatch({
            type: EActionType.SetToastMessage,
            payload: undefined,
        });
        goBack(callFromSiblingRoute);
    };

    const submitForm = async ({
        formData,
        userVerification,
        skipVerificationStep = false,
    }: {
        formData: Record<string, any>;
        userVerification: IUserVerification;
        skipVerificationStep?: boolean;
    }) => {
        try {
            dispatch({
                type: EActionType.SetToastMessage,
                payload: undefined,
            });

            dispatch({
                type: EActionType.SetEstimations,
                payload: {
                    ...estimations,
                    isFetching: true,
                },
            });

            const {
                validationMethod,
                verificationRequestId,
                phoneNumber: rawPhoneNumber,
            } = userVerification;

            const estimationsResponse = await submitFormattedForm(
                {
                    ...formData,
                    // We check the email to know if we've edited the personnal informations, otherwise we keep previous data
                    ...(!formData.email && { keepPreviousPropertyCharacteristics: true }),
                    ...(validationMethod && {
                        phoneNumberValidationMethod: validationMethod,
                    }),
                    ...(verificationRequestId && {
                        phoneNumberVerificationRequestId: verificationRequestId,
                    }),
                    ...(rawPhoneNumber && {
                        phoneNumber: extractPhoneNumber(rawPhoneNumber).phoneNumber,
                        phoneNumberRegionCode:
                            extractPhoneNumber(rawPhoneNumber).phoneNumberRegionCode,
                    }),
                },
                config,
                name,
                locale,
                dispatch,
            );

            stopTimer();

            dispatch({
                type: EActionType.SetEstimations,
                payload: {
                    ...estimationsResponse,
                    isFetching: false,
                },
            });

            CustomEventService.fire(
                IEvents.SEND_TRACKING_EVENT,
                composeInteractionPayload({
                    eventName: EInteractionEvents.Confirmation,
                    formValues: formData,
                    portal,
                    estimation: estimationsResponse,
                }),
            );

            // Skip verification step (sms validation page)
            if (skipVerificationStep) {
                navigate('../loading');
                return;
            }

            goNext();
        } catch (e: any) {
            if (name === 'verification') {
                CustomEventService.fire(
                    IEvents.SEND_TRACKING_EVENT,
                    composeMPUInteractionPayload({
                        eventName: EMonthlyPriceUpdateInteractionEvents.MFAError,
                        formValues: formData,
                        category: EAvCategory.Error,
                        portal,
                        rawData: {
                            av_error_code: e.message,
                        },
                    }),
                );
            }

            dispatch({
                type: EActionType.SetToastMessage,
                payload: { status: e.message },
            });
            dispatch({
                type: EActionType.SetUserVerification,
                payload: {
                    ...userVerification,
                    isFetching: false,
                },
            });
            dispatch({
                type: EActionType.SetEstimations,
                payload: { ...estimations, isFetching: false },
            });

            if (name !== 'verification') {
                // display the error toast on verification step
                throw new Error(e);
            }
        }
    };

    const handleNext = async (formData?: Record<string, any>) => {
        if (!customSubmit || !formData) {
            goNext();
            return;
        }

        const callVerificationServiceAndSubmit = async () => {
            try {
                dispatch({
                    type: EActionType.SetToastMessage,
                    payload: undefined,
                });

                dispatch({
                    type: EActionType.SetUserVerification,
                    payload: {
                        ...userVerification,
                        isFetching: true,
                    },
                });

                //customSubmit can be useSmsService | verification
                //TODO refactor to avoid using customSubmit (only useSmsService is possible now)
                const response = await customSubmit(formData, config, name, locale);

                dispatch({
                    type: EActionType.SetUserVerification,
                    payload: {
                        ...response,
                        isFetching: false,
                    },
                });

                // If user needs to validate the phone number, go to verification step first
                if (response?.isVerificationRequired) {
                    startTimer();
                    const verificationStepIndex = stepNames.indexOf('verification');
                    goTo(verificationStepIndex);

                    // This is an edge case where the user has previously estimated a property with a landline phonenumber.
                    if (
                        isFromUpdatePage &&
                        response.status === ResultStatus.NotAMobilePhoneNumber &&
                        response.validationMethod === ValidationMethods.UnableToVerifyAtCreation
                    ) {
                        /* User is on the update page but has now to verify the number. 
                        User has to edit it to provide a mobile one. A warning is displayed to inform.
                        Note that the ui will be the same as if an sms code had been sent to landline number*/
                        dispatch({
                            type: EActionType.SetToastMessage,
                            payload: {
                                type: 'warning',
                                status: response.status,
                                persistent: true,
                            },
                        });
                    }
                }
                // Otherwise, if validated or not required or unavailable, submit the form
                else
                    await submitForm({
                        formData,
                        userVerification: response,
                        skipVerificationStep: true,
                    });
            } catch (e: any) {
                dispatch({
                    type: EActionType.SetUserVerification,
                    payload: {
                        ...userVerification,
                        isFetching: false,
                    },
                });

                /* TODO Check if we want to handle this edge case: the api can respond "conflict" when the timer is not running
                On update for eg: if the user request the phone verification without entering the code and refresh the page,
                To go on, the response will be "conflict" but the countdown will be reset and so will be the userVerification
                So the user will be blocked until the coutdown is over */
                if (
                    e.message === ResultStatus.Conflict &&
                    isTimerRunning &&
                    userVerification.isVerificationRequired
                ) {
                    /* On v2 only (using Vonage api), this error can be catched: 
                    It means that the countdown is still running and previously sent sms code is still valid.
                    Situation is, the user wanted back on identification step and is trying 
                    one more time to validate the phone number without any changes in the form.  
                    So, let's go back to verification step and display the countdown still running */
                    const verificationStepIndex = stepNames.indexOf('verification');
                    goTo(verificationStepIndex);
                    return;
                } else {
                    dispatch({
                        type: EActionType.SetToastMessage,
                        payload: { status: e.message },
                    });
                }
            }
        };

        switch (name) {
            case 'update':
            case 'identity': {
                await callVerificationServiceAndSubmit();
                break;
            }
            case 'verification': {
                await submitForm({ formData, userVerification });

                CustomEventService.fire(
                    IEvents.SEND_TRACKING_EVENT,
                    composeMPUInteractionPayload({
                        eventName: EMonthlyPriceUpdateInteractionEvents.MFASuccess,
                        formValues: formData,
                        category: EAvCategory.Account,
                        portal,
                        rawData: {
                            method: userVerification.validationMethod,
                            link_text: 'verify',
                        },
                    }),
                );
                break;
            }
            default:
                break;
        }
    };

    // TODO [WLEF-482] understand why we need switch case
    const handleNavigation = async (type: string, formData?: Record<string, any>) => {
        switch (type) {
            case 'popstate':
                handleBack(false);
                break;
            case 'next':
                handleNext(formData);
                break;
            case 'first':
                goToFirstPage();
                break;
            default:
                break;
        }
    };

    return { handleNavigation, handleBack, submitForm };
};

export default useNavigation;
